import posthog from 'posthog-js';
import type { CaptureResult, Properties } from 'posthog-js';
import { useEffect, useState } from 'react';

import { scrubUrl } from './routePattern';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

/**
 * Event properties that carry a URL. Two things in ours are secrets: search
 * terms and onboarding tokens in the query string (`?q=<patient name>`), and
 * follow-up tokens in the PATH (`/followup/<token>`, a working access
 * credential). Both are removed before the event leaves the browser — the
 * route pattern alone is enough to analyze funnels and page usage.
 */
const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$pathname',
  '$initial_pathname',
  // Session-entry props ride on EVERY event of the session, and the previous
  // pathname on each pageview — so a token on the landing URL would otherwise
  // repeat on every funnel step that follows it.
  '$session_entry_url',
  '$session_entry_pathname',
  '$session_entry_referrer',
  '$prev_pageview_pathname',
];

const scrubUrlProperties = (properties: Properties): Properties => {
  const scrubbed = { ...properties };
  for (const key of URL_PROPERTIES) {
    const value = scrubbed[key];
    if (typeof value === 'string') {
      scrubbed[key] = scrubUrl(value);
    }
  }
  return scrubbed;
};

const WEB_VITALS_METRIC_KEY = /^\$web_vitals_[A-Z]+_event$/;

/**
 * Each `$web_vitals` event nests one object per metric, and posthog-js writes
 * a raw `$current_url` into every one of them — below where the top-level
 * scrub looks. With attribution on, `attribution.url` is the LCP resource,
 * which on a records page is a presigned S3 URL for a patient document. Only
 * primitive attribution fields survive (element selector, timings); `url` and
 * every entry object are dropped.
 */
const scrubWebVitalsMetrics = (properties: Properties): Properties => {
  const scrubbed = { ...properties };
  for (const key of Object.keys(scrubbed)) {
    if (!WEB_VITALS_METRIC_KEY.test(key)) continue;
    const metric = scrubbed[key];
    if (!metric || typeof metric !== 'object') continue;
    const { attribution, ...rest } = metric as Record<string, unknown>;
    scrubbed[key] = {
      ...scrubUrlProperties(rest),
      ...(attribution && typeof attribution === 'object'
        ? {
            attribution: Object.fromEntries(
              Object.entries(attribution).filter(
                ([k, v]) =>
                  k !== 'url' &&
                  (typeof v === 'string' ||
                    typeof v === 'number' ||
                    typeof v === 'boolean')
              )
            ),
          }
        : {}),
    };
  }
  return scrubbed;
};

/**
 * `before_send` hook — the last point where an event can be edited in the
 * browser. Two de-identification rules apply to every event (we have no BAA
 * with PostHog, so the event stream must stay free of PHI):
 *
 * 1. `$geoip_disable` stops PostHog's server-side city/region/lat-lng
 *    derivation, which would otherwise pair a person-level identifier with
 *    health-seeking context (PHI under OCR's online-tracking guidance).
 *    Raw IP storage is a project-level setting — "Discard client IP data"
 *    must also be enabled in the PostHog project.
 * 2. URL query strings are stripped (see {@link URL_PROPERTIES}).
 */
export const deidentifyEvent = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) {
    return null;
  }

  return {
    ...event,
    properties: {
      ...scrubWebVitalsMetrics(scrubUrlProperties(event.properties)),
      $geoip_disable: true,
    },
    ...(event.$set ? { $set: scrubUrlProperties(event.$set) } : {}),
    ...(event.$set_once
      ? { $set_once: scrubUrlProperties(event.$set_once) }
      : {}),
  };
};

/**
 * Marks a subtree whose text is app-authored chrome (nav labels, field
 * labels, button copy, tab names) and is therefore recorded verbatim in
 * session replay. Everything else stays masked.
 *
 * Only put this on elements whose text comes from the codebase, never from
 * the API. If a component renders anything a clinic typed or a patient
 * supplied, it does not get this attribute.
 */
export const UNMASK_ATTRIBUTE = 'data-ph-unmask';

/** Spread onto the element whose subtree should stay readable in replay. */
export const UNMASK_PROPS = { [UNMASK_ATTRIBUTE]: '' };

/**
 * Marks a subtree that replay replaces with a placeholder box — not masked
 * text, no DOM at all. For rendered medical records (document thumbnails,
 * PDF and image previews), where the recording would otherwise carry
 * presigned S3 URLs that resolve to the record itself.
 */
export const BLOCK_ATTRIBUTE = 'data-ph-block';

/** Spread onto the element whose subtree replay should not record at all. */
export const BLOCK_PROPS = { [BLOCK_ATTRIBUTE]: '' };

/**
 * Re-masks a subtree that sits inside an unmasked one. Shared chrome is a
 * container — a tab strip, a nav — and containers accumulate API-fed children
 * like counts. Without this, the only choices would be leaking those children
 * or unmasking nothing at all.
 *
 * The innermost marker wins, so this can be nested inside UNMASK_ATTRIBUTE
 * (and re-unmasked below that, if it ever comes up).
 */
export const MASK_ATTRIBUTE = 'data-ph-mask';

/** Spread onto API-fed content rendered inside an unmasked subtree. */
export const MASK_PROPS = { [MASK_ATTRIBUTE]: '' };

/**
 * Replay's per-text-node masking hook. rrweb only calls this for nodes it has
 * already decided to mask — which, with `maskTextSelector: '*'`, is all of
 * them — so returning the original text is what opts a subtree out.
 *
 * Mirrors rrweb's own masking (every non-whitespace character becomes `*`) so
 * masked text keeps the shape of the layout it replaced.
 */
export const maskText = (text: string, element?: HTMLElement): string => {
  // Nearest marker wins: an unmasked container can re-mask the API-fed parts
  // of its subtree, so `closest` has to consider both attributes at once
  // rather than checking for the unmask alone.
  const marker = element?.closest(`[${UNMASK_ATTRIBUTE}], [${MASK_ATTRIBUTE}]`);
  return marker?.hasAttribute(UNMASK_ATTRIBUTE)
    ? text
    : text.replace(/\S/g, '*');
};

/**
 * Start PostHog. No-op when no project key is configured (local dev), so every
 * call below is safe to make unconditionally.
 *
 * When the app is embedded in the marketing site's onboarding iframe the parent
 * passes its own anonymous id as `?deviceId=`, which we bootstrap as PostHog's
 * distinct id so both halves of the funnel land on one session. Bootstrapping
 * only takes effect for a browser PostHog hasn't seen before — a returning
 * visitor keeps the id already in storage.
 */
export const initAnalytics = () => {
  if (!POSTHOG_KEY) {
    return;
  }

  const parentDistinctId = new URLSearchParams(window.location.search).get(
    'deviceId'
  );

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-06-25',
    // Autocapture records clicked-element text and form field metadata. On
    // this app's pages that text can contain PHI (patient names in rows and
    // buttons), so it stays off — funnels emit explicit trackEvent calls
    // instead. Heatmaps and dead clicks capture the same element context.
    autocapture: false,
    capture_heatmaps: false,
    capture_dead_clicks: false,
    // Wizard pages are single-URL, so step progression still needs explicit
    // events; this only covers real route changes.
    capture_pageview: 'history_change',
    // LCP/FCP/CLS/INP on every page load, on the same distinct_id as the
    // funnel events, so a slow load can be tied to a drop-off. Set here rather
    // than by the project toggle so it is visible in code. Attribution names
    // the LCP element and the CLS sources at the cost of the larger
    // web-vitals build.
    capture_performance: {
      web_vitals: true,
      web_vitals_attribution: true,
      // The extension sends only when all four metrics are in or this timer
      // fires, and never on page hide. Safari reports no CLS or INP, so its
      // buffer never fills; at the 5s default any leave inside the first
      // seconds loses the event. FCP and LCP are in well before this.
      web_vitals_delayed_flush_ms: 1500,
    },
    // Anonymous visitors get no person profile — only users we explicitly
    // identify after login do.
    person_profiles: 'identified_only',
    session_recording: {
      // Typed values are never readable: staff type patient names, DOBs and
      // addresses into these forms.
      maskAllInputs: true,
      // '*' masks every text node, and maskTextFn then hands back the
      // readable text for the allowlisted subtrees (see maskText). The
      // selector stays '*' so the DEFAULT is masked: a component added
      // tomorrow that renders patient data is covered without anyone
      // remembering to mask it.
      maskTextSelector: '*',
      maskTextFn: maskText,
      blockSelector: `[${BLOCK_ATTRIBUTE}]`,
    },
    before_send: deidentifyEvent,
    ...(parentDistinctId
      ? { bootstrap: { distinctID: parentDistinctId } }
      : {}),
  });
};

/**
 * Fire an explicit event. Autocapture is off and wizard steps share a single
 * URL, so funnels must emit their own step events.
 *
 * `options` is PostHog's capture options; the last event before a page goes
 * away takes `TEARDOWN_CAPTURE`.
 */
export const trackEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>,
  options?: Parameters<typeof posthog.capture>[2]
) => {
  if (!POSTHOG_KEY) {
    return;
  }
  posthog.capture(eventName, eventProperties, options);
};

/**
 * Capture options for an event fired right before the document goes away (an
 * embedded wizard posting BOOKING_COMPLETE makes the parent tear the iframe
 * down). `send_instantly` only skips the batch queue; the XHR it issues dies
 * with the document, and `sendBeacon` is the only transport a browser still
 * delivers after that.
 */
export const TEARDOWN_CAPTURE: Parameters<typeof trackEvent>[2] = {
  send_instantly: true,
  transport: 'sendBeacon',
};

/** Attach the authenticated user to the current session. */
export const identifyUser = (
  userId: string,
  userProperties?: Record<string, unknown>
) => {
  if (!POSTHOG_KEY) {
    return;
  }
  posthog.identify(userId, userProperties);
};

/** Drop the identified user on logout — the next person on this browser starts clean. */
export const resetAnalytics = () => {
  if (!POSTHOG_KEY) {
    return;
  }
  posthog.reset();
};

/**
 * Whether a PostHog feature flag is on for this visitor. False until flags
 * load, so a flagged surface appears rather than disappears. With no project
 * key (local dev) every flag is on, so flagged work stays testable.
 */
export function useFeatureFlag(flag: string): boolean {
  const [enabled, setEnabled] = useState(() =>
    POSTHOG_KEY ? posthog.isFeatureEnabled(flag) === true : true
  );

  useEffect(() => {
    if (!POSTHOG_KEY) {
      return;
    }
    return posthog.onFeatureFlags(() =>
      setEnabled(posthog.isFeatureEnabled(flag) === true)
    );
  }, [flag]);

  return enabled;
}
